import { useCallback, useMemo } from "react";

import { useLocalStorage } from "@/modern/hooks/useLocalStorage";

import { changelog } from "./changelogData";
import {
  allEntryIds,
  getAllVersionsNewestFirst,
  getUnreadCount,
} from "./changelogSelectors";

export const CHANGELOG_SEEN_STORAGE_KEY = "swivu:changelog-seen-v1";

export type ChangelogSeenState = {
  seenEntryIds: string[];
};

const INITIAL_SEEN: ChangelogSeenState = {
  seenEntryIds: [],
};

/** Static changelog ids — computed once for mark-seen / unread snapshots. */
const ALL_ENTRY_IDS = allEntryIds(changelog);

export function useChangelogSeen() {
  const [seen, setSeen] = useLocalStorage<ChangelogSeenState>(
    CHANGELOG_SEEN_STORAGE_KEY,
    INITIAL_SEEN
  );

  const seenEntryIds = seen.seenEntryIds ?? [];

  const unreadCount = useMemo(
    () => getUnreadCount(changelog, seenEntryIds),
    [seenEntryIds]
  );

  const allVersions = useMemo(() => getAllVersionsNewestFirst(changelog), []);

  const markSeen = useCallback(() => {
    if (
      seenEntryIds.length === ALL_ENTRY_IDS.length &&
      ALL_ENTRY_IDS.every((id) => seenEntryIds.includes(id))
    ) {
      return;
    }
    setSeen({ seenEntryIds: ALL_ENTRY_IDS });
  }, [seenEntryIds, setSeen]);

  const markEntriesSeen = useCallback(
    (entryIds: string[]) => {
      if (entryIds.length === 0) return;
      const next = Array.from(new Set([...seenEntryIds, ...entryIds]));
      if (next.length === seenEntryIds.length) return;
      setSeen({ seenEntryIds: next });
    },
    [seenEntryIds, setSeen]
  );

  return {
    seenEntryIds,
    unreadCount,
    allVersions,
    allEntryIds: ALL_ENTRY_IDS,
    markSeen,
    markEntriesSeen,
  };
}
